import axios, { AxiosInstance } from 'axios';
import { User, AuthTokens } from '../types';
import { getAuthStorage } from '../utils/sessionPreferences';
import { clearStoredSsoLoginHint } from '../utils/ssoLoginHint';
import { logger } from '../utils/logger';

const AUTH_STORAGE_KEYS = [
    'auth_tokens',
    'user_data',
    'user_email',
    'username',
    'user_level',
    'user_can_share'
];

interface FetchBootstrapOptions {
    suppressAuthFailure?: boolean;
}

class AuthService {
    private static instance: AuthService;
    private api: AxiosInstance;
    private tokens: AuthTokens | null = null;
    private refreshPromise: Promise<boolean> | null = null;
    private domain: string;
    private tokenExpiryCallback: (() => void) | null = null;
    private userChangeCallback: (() => void) | null = null;

    private constructor() {
        this.domain = this.getDomainFromStorage();
        this.api = axios.create({
            baseURL: this.domain,
            timeout: 30000,
        });

        this.setupInterceptors();
        this.loadTokensFromStorage();
    }

    static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    public setTokenExpiryCallback(callback: () => void): void {
        this.tokenExpiryCallback = callback;
    }

    public setUserChangeCallback(callback: () => void): void {
        this.userChangeCallback = callback;
    }

    private getDomainFromStorage(): string {
        const savedDomain = localStorage.getItem('api_domain');
        return savedDomain || 'https://server1.notizy.nl';
    }

    public async setDomain(domain: string): Promise<void> {
        // Format domain properly
        let formattedDomain = domain;
        if (!formattedDomain.startsWith('http://') && !formattedDomain.startsWith('https://')) {
            formattedDomain = `https://${formattedDomain}`;
        }

        try {
            // Valideer of het een geldige URL is
            new URL(formattedDomain);
        } catch (error) {
            logger.error('Invalid domain format:', error);
            throw new Error('Ongeldig domein formaat');
        }

        this.domain = formattedDomain;
        this.api.defaults.baseURL = formattedDomain;
        localStorage.setItem('api_domain', formattedDomain);
    }

    public getDomain(): string {
        return this.domain;
    }

    public async checkDomainStatus(): Promise<boolean> {
        try {
            const response = await axios.get(`${this.domain}/alive`, { timeout: 3000 });
            return response.status === 200;
        } catch (error) {
            return false;
        }
    }

    public async determineLoginMethod(email: string): Promise<{ method: 'code' | 'password' | 'both' | 'sso'; orgIdentifier?: string }> {
        try {
            const response = await this.api.post('/determine-login-method', {
                email
            }, { timeout: 10000 });

            if (response.status === 200) {
                const { method, org_identifier } = response.data;

                return {
                    method,
                    orgIdentifier: org_identifier
                };
            }
            throw new Error('Kon login methode niet bepalen');
        } catch (error) {
            logger.error('Error determining login method:', error);
            if (axios.isAxiosError(error) && error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw new Error('Kon login methode niet bepalen. Probeer het opnieuw.');
        }
    }

    public async requestLoginCode(email: string): Promise<boolean> {
        try {
            const response = await this.api.post('/request-login-code', {
                email
            });
            return response.status === 200;
        } catch (error) {
            logger.error('Error requesting login code:', error);
            return false;
        }
    }

    public async verifyPassword(email: string, password: string): Promise<boolean> {
        try {
            const response = await this.api.post('/verify-password', {
                email,
                password
            });

            if (response.status === 200) {
                const { access_token, refresh_token, level } = response.data;

                const tokens: AuthTokens = {
                    accessToken: access_token,
                    refreshToken: refresh_token,
                    expiresAt: new Date(Date.now() + 2 * 60 * 1000), // 2 minuten
                };

                this.saveTokensToStorage(tokens);

                // Fallback: set basic storage keys for legacy compat
                const storage = getAuthStorage();
                storage.setItem('user_email', email);
                storage.setItem('username', email);
                storage.setItem('user_level', level || 'user');
                this.updateUserCanShareFromToken(access_token);

                // Bootstrap user data in the background so a transient /api/me failure
                // does not invalidate an otherwise successful login.
                void this.fetchBootstrap({ suppressAuthFailure: true });

                return true;
            }
            return false;
        } catch (error) {
            logger.error('Password verification error:', error);
            if (axios.isAxiosError(error) && error.response) {
                const status = error.response.status;
                if (status === 401) {
                    throw new Error('Onjuist e-mailadres of wachtwoord.');
                } else if (status === 403) {
                    throw new Error('Je account is geblokkeerd.');
                } else if (status === 429) {
                    throw new Error('Te veel pogingen. Probeer het morgen opnieuw.');
                }
            }
            throw new Error('Er is een fout opgetreden. Controleer je internetverbinding.');
        }
    }

    public async requestPasswordReset(email: string): Promise<string> {
        try {
            const response = await this.api.post('/request-password-reset', { email });
            return response.data?.message || 'Als het e-mailadres bekend is en wachtwoord login actief is, ontvang je een e-mail met een reset link.';
        } catch (error) {
            // Altijd een generieke melding retourneren (voorkomt user enumeration)
            return 'Als het e-mailadres bekend is en wachtwoord login actief is, ontvang je een e-mail met een reset link.';
        }
    }

    public async verifyResetToken(token: string): Promise<{ valid: boolean; email?: string }> {
        try {
            const response = await this.api.post('/verify-reset-token', { token });
            if (response.status === 200) {
                return { valid: true, email: response.data?.email };
            }
            return { valid: false };
        } catch (error) {
            return { valid: false };
        }
    }

    public async resetPassword(token: string, password: string): Promise<void> {
        try {
            await this.api.post('/reset-password', { token, password });
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const status = error.response.status;
                if (status === 400) {
                    throw new Error('Wachtwoord moet minimaal 8 tekens bevatten.');
                } else if (status === 403) {
                    throw new Error('Je account is geblokkeerd.');
                } else if (status === 410) {
                    throw new Error('De reset link is verlopen. Vraag een nieuwe aan.');
                }
            }
            throw new Error('Er is een fout opgetreden. Probeer het opnieuw.');
        }
    }

    public async verifyLoginCode(email: string, code: string): Promise<boolean> {
        try {
            const response = await this.api.post('/verify-login-code', {
                email,
                code
            });

            if (response.status === 200) {
                const { access_token, refresh_token, level } = response.data;

                const tokens: AuthTokens = {
                    accessToken: access_token,
                    refreshToken: refresh_token,
                    expiresAt: new Date(Date.now() + 2 * 60 * 1000), // 2 minuten
                };

                this.saveTokensToStorage(tokens);

                // Fallback: set basic storage keys for legacy compat
                const storage = getAuthStorage();
                storage.setItem('user_email', email);
                storage.setItem('username', email);
                storage.setItem('user_level', level || 'user');
                this.updateUserCanShareFromToken(access_token);

                // Bootstrap user data in the background so a transient /api/me failure
                // does not invalidate an otherwise successful login.
                void this.fetchBootstrap({ suppressAuthFailure: true });
                clearStoredSsoLoginHint();

                return true;
            }
            return false;
        } catch (error) {
            logger.error('Code verification error:', error);
            return false;
        }
    }

    public getSsoLoginUrl(orgIdentifier: string): string {
        return `${this.domain}/sso/login/${orgIdentifier}?platform=web`;
    }

    public async exchangeSsoCode(code: string): Promise<boolean> {
        try {
            const response = await this.api.post('/sso/exchange-code', {
                code
            });

            if (response.status === 200) {
                const { access_token, refresh_token, level, can_share } = response.data;

                const tokens: AuthTokens = {
                    accessToken: access_token,
                    refreshToken: refresh_token,
                    expiresAt: new Date(Date.now() + 2 * 60 * 1000), // 2 minuten
                };

                this.saveTokensToStorage(tokens);
                this.updateUserCanShareFromToken(access_token);

                // Maak direct een minimale gebruiker uit de JWT-claims zodat de
                // route guard niet terugvalt naar /login terwijl /api/me nog laadt.
                try {
                    const fallbackUser = this.buildFallbackUserFromToken(access_token, {
                        level,
                        canShare: can_share
                    });

                    if (fallbackUser) {
                        this.persistFallbackUser(fallbackUser);
                    }
                } catch (e) {
                    logger.warn('Could not extract user info from token', e);
                }

                // Bootstrap user data in the background so a transient /api/me failure
                // does not invalidate an otherwise successful login.
                void this.fetchBootstrap({ suppressAuthFailure: true });
                clearStoredSsoLoginHint();

                return true;
            }
            return false;
        } catch (error) {
            logger.error('SSO code exchange error:', error);
            return false;
        }
    }

    private setupInterceptors() {
        // Request interceptor voor het toevoegen van auth token
        this.api.interceptors.request.use(
            async (config) => {
                const token = await this.getValidAccessToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                // Voeg App-Version header toe aan alle requests
                config.headers['App-Version'] = 'web';
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor voor het afhandelen van 401 errors
        this.api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Login-gerelateerde endpoints moeten NIET door de token-refresh logica gaan.
                // Een 401 van /verify-password is een "fout wachtwoord", geen verlopen token.
                const loginEndpoints = ['/verify-password', '/verify-login-code', '/determine-login-method', '/login', '/request-login-code', '/request-password-reset', '/verify-reset-token', '/reset-password'];
                const requestUrl = originalRequest.url || '';
                const isLoginEndpoint = loginEndpoints.some(ep => requestUrl.endsWith(ep));

                if (isLoginEndpoint) {
                    return Promise.reject(error);
                }

                // Check voor token expiry in response body (naast 401 status)
                const isTokenExpired = error.response?.status === 401 ||
                    (error.response?.data?.error === 'token_expired') ||
                    (error.response?.data?.msg === 'Token has expired');

                if (isTokenExpired && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        const refreshed = await this.refreshTokens();
                        if (refreshed) {
                            const token = await this.getValidAccessToken();
                            if (token) {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                return this.api(originalRequest);
                            }
                        }
                    } catch (refreshError) {
                        logger.error('Token refresh failed, logging out user:', refreshError);
                        this.handleTokenExpiry();
                        return Promise.reject(error);
                    }

                    // Als we hier komen, is de token refresh gefaald
                    logger.warn('Token expired and refresh failed, redirecting to login');
                    this.handleTokenExpiry();
                }

                return Promise.reject(error);
            }
        );
    }

    private clearAuthStorage() {
        AUTH_STORAGE_KEYS.forEach((key) => {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                logger.warn('Error clearing local auth storage:', error);
            }

            try {
                sessionStorage.removeItem(key);
            } catch (error) {
                logger.warn('Error clearing session auth storage:', error);
            }
        });
    }

    private clearAuthTokens() {
        try {
            localStorage.removeItem('auth_tokens');
        } catch (error) {
            logger.warn('Error clearing local auth tokens:', error);
        }

        try {
            sessionStorage.removeItem('auth_tokens');
        } catch (error) {
            logger.warn('Error clearing session auth tokens:', error);
        }
    }

    private loadTokensFromStorage() {
        try {
            const storage = getAuthStorage();
            const tokensJson = storage.getItem('auth_tokens');
            if (tokensJson) {
                const tokens = JSON.parse(tokensJson);
                this.tokens = {
                    ...tokens,
                    expiresAt: new Date(tokens.expiresAt)
                };
            }
        } catch (error) {
            logger.error('Error loading tokens from storage:', error);
            try {
                const storage = getAuthStorage();
                storage.removeItem('auth_tokens');
            } catch (clearError) {
                logger.warn('Error clearing auth token storage:', clearError);
            }
        }
    }

    private saveTokensToStorage(tokens: AuthTokens) {
        try {
            this.clearAuthTokens();
            const storage = getAuthStorage();
            storage.setItem('auth_tokens', JSON.stringify(tokens));
            this.tokens = tokens;
        } catch (error) {
            logger.error('Error saving tokens to storage:', error);
        }
    }

    private getStringClaim(claims: Record<string, any> | null, keys: string[]): string | undefined {
        if (!claims) {
            return undefined;
        }

        for (const key of keys) {
            const value = claims[key];
            if (typeof value === 'string') {
                const trimmedValue = value.trim();
                if (trimmedValue) {
                    return trimmedValue;
                }
            }
        }

        return undefined;
    }

    private decodeJwtClaims(token: string): Record<string, any> | null {
        try {
            const segments = token.split('.');
            if (segments.length < 2) {
                return null;
            }

            const payloadSegment = segments[1].replace(/-/g, '+').replace(/_/g, '/');
            const padding = payloadSegment.length % 4;
            const paddedPayload = padding ? `${payloadSegment}${'='.repeat(4 - padding)}` : payloadSegment;

            let decoded = '';
            if (typeof atob === 'function') {
                decoded = atob(paddedPayload);
            } else if (typeof Buffer !== 'undefined') {
                decoded = Buffer.from(paddedPayload, 'base64').toString('utf-8');
            }

            if (!decoded) {
                return null;
            }

            return JSON.parse(decoded);
        } catch (error) {
            logger.error('Error decoding token claims:', error);
            return null;
        }
    }

    private buildFallbackUserFromToken(token: string, options: { level?: string; canShare?: boolean } = {}): User | null {
        const claims = this.decodeJwtClaims(token);
        const username = this.getStringClaim(claims, ['username', 'email', 'sub']);
        const email = this.getStringClaim(claims, ['email', 'username', 'sub']);

        if (!username && !email) {
            return null;
        }

        const level = this.getStringClaim(claims, ['level']) || options.level || 'user';
        const canShare = typeof claims?.can_share === 'boolean' ? claims.can_share : options.canShare;

        return {
            id: username || email || '1',
            username: username || email || '',
            email: email || username,
            level,
            canShare,
            user_type: this.getStringClaim(claims, ['user_type']),
            is_admin: typeof claims?.is_admin === 'boolean' ? claims.is_admin : undefined,
            is_org_admin: typeof claims?.is_org_admin === 'boolean' ? claims.is_org_admin : undefined,
        };
    }

    private persistFallbackUser(user: User) {
        const storage = getAuthStorage();

        storage.setItem('user_data', JSON.stringify(user));
        storage.setItem('username', user.username);
        storage.setItem('user_level', user.level);

        if (user.email) {
            storage.setItem('user_email', user.email);
        }

        if (typeof user.canShare === 'boolean') {
            storage.setItem('user_can_share', user.canShare ? 'true' : 'false');
        }
    }

    private updateUserCanShareFromToken(token?: string | null) {
        if (!token) {
            return;
        }

        const claims = this.decodeJwtClaims(token);
        if (!claims) {
            return;
        }

        // Als can_share niet aanwezig is of null is, doen we niets
        // Dit voorkomt dat we een bestaande permissie overschrijven met 'false'
        if (typeof claims.can_share === 'undefined' || claims.can_share === null) {
            return;
        }

        const canShare = !!claims.can_share;

        try {
            const storage = getAuthStorage();
            storage.setItem('user_can_share', canShare ? 'true' : 'false');

            const userDataRaw = storage.getItem('user_data');
            if (userDataRaw) {
                const parsed = JSON.parse(userDataRaw);
                parsed.canShare = canShare;
                storage.setItem('user_data', JSON.stringify(parsed));
                storage.setItem('user_data', JSON.stringify(parsed));
            }

            // Trigger update voor luisteraars (zoals AuthContext)
            if (this.userChangeCallback) {
                this.userChangeCallback();
            }
        } catch (error) {
            logger.error('Error updating can_share flag:', error);
        }
    }

    private getStoredCanShare(): boolean | null {
        try {
            const storage = getAuthStorage();
            const stored = storage.getItem('user_can_share');
            if (stored === null) {
                return null;
            }
            return stored === 'true';
        } catch (error) {
            logger.error('Error reading can_share flag:', error);
            return null;
        }
    }

    async login(username: string, password: string): Promise<boolean> {
        try {
            const response = await this.api.post('/login', {
                username,
                password
            });

            if (response.status === 200) {
                const { access_token, refresh_token, level } = response.data;

                const tokens: AuthTokens = {
                    accessToken: access_token,
                    refreshToken: refresh_token,
                    expiresAt: new Date(Date.now() + 2 * 60 * 1000), // 2 minuten
                };

                this.saveTokensToStorage(tokens);
                this.updateUserCanShareFromToken(access_token);

                // Fallback: set basic storage keys for legacy compat
                const storage = getAuthStorage();
                storage.setItem('username', username);
                storage.setItem('user_level', level || 'user');

                // Bootstrap user data in the background so a transient /api/me failure
                // does not invalidate an otherwise successful login.
                void this.fetchBootstrap({ suppressAuthFailure: true });
                clearStoredSsoLoginHint();

                return true;
            }
            return false;
        } catch (error) {
            logger.error('Login error:', error);
            return false;
        }
    }

    async logout() {
        this.tokens = null;
        this.clearAuthStorage();
    }

    public handleTokenExpiry() {
        // Roep de callback aan om de AuthContext op de hoogte te stellen
        if (this.tokenExpiryCallback) {
            this.tokenExpiryCallback();
        }

        // Logout uitvoeren
        this.logout();

        // Alleen doorverwijzen naar login pagina als we niet al op de settings pagina zijn
        // Dit voorkomt redirects als je de server settings probeert te bereiken zonder ingelogd te zijn
        if (!window.location.pathname.includes('/settings')) {
            // Doorverwijzen naar login pagina
            // Gebruik window.location om een volledige pagina refresh te forceren
            // zodat de AuthContext ook wordt gereset
            window.location.href = '/login';
        }
    }

    async getValidAccessToken(): Promise<string | null> {
        if (!this.tokens) return null;

        // Controleer of token nog geldig is (met 30 seconden buffer)
        const now = new Date();
        const expiresWithBuffer = new Date(this.tokens.expiresAt.getTime() - 30000);

        if (now < expiresWithBuffer) {
            return this.tokens.accessToken;
        }

        // Token is verlopen, probeer te vernieuwen
        const refreshed = await this.refreshTokens();
        return refreshed ? this.tokens?.accessToken || null : null;
    }

    private async refreshTokens(): Promise<boolean> {
        // Voorkom meerdere gelijktijdige refresh requests
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        if (!this.tokens?.refreshToken) {
            return false;
        }

        this.refreshPromise = this.performTokenRefresh();
        const result = await this.refreshPromise;
        this.refreshPromise = null;
        return result;
    }

    private async performTokenRefresh(): Promise<boolean> {
        try {
            const response = await axios.post(
                `${this.api.defaults.baseURL}/refresh`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${this.tokens?.refreshToken}`
                    }
                }
            );

            if (response.status === 200) {
                const { access_token, refresh_token } = response.data;

                const newTokens: AuthTokens = {
                    accessToken: access_token,
                    refreshToken: refresh_token,
                    expiresAt: new Date(Date.now() + 2 * 60 * 1000),
                };

                this.saveTokensToStorage(newTokens);
                this.updateUserCanShareFromToken(access_token);

                // Refresh bootstrap in the background; a failing profile refresh should
                // not immediately throw the user back to the login screen.
                void this.fetchBootstrap({ suppressAuthFailure: true });

                return true;
            }
            return false;
        } catch (error) {
            logger.error('Token refresh error:', error);
            return false;
        }
    }

    isLoggedIn(): boolean {
        return this.tokens !== null;
    }

    getApi(): AxiosInstance {
        return this.api;
    }

    /**
     * Fetch user bootstrap from GET /api/me.
     * Stores the full user object in user_data and syncs legacy storage keys.
     * Fails silently — legacy localStorage fallback remains functional.
     */
    public async fetchBootstrap(options: FetchBootstrapOptions = {}): Promise<User | null> {
        try {
            const { suppressAuthFailure = false } = options;
            const response = suppressAuthFailure
                ? await this.fetchBootstrapWithoutInterceptor()
                : await this.api.get('/api/me');

            if (response.status === 200 && response.data) {
                const data = response.data;
                const storage = getAuthStorage();

                const user: User = {
                    id: data.user || data.email || '1',
                    username: data.user || data.email || '',
                    level: data.level || 'user',
                    email: data.email || data.user || '',
                    canShare: data.can_share ?? data.capabilities?.can_share ?? undefined,
                    user_type: data.user_type,
                    is_admin: data.is_admin,
                    is_org_admin: data.is_org_admin,
                    organization_id: data.organization_id,
                    group_id: data.group_id,
                    capabilities: data.capabilities,
                    frontend_settings: data.frontend_settings,
                };

                // Persist full user object
                storage.setItem('user_data', JSON.stringify(user));

                // Sync legacy keys
                if (user.email) {
                    storage.setItem('user_email', user.email);
                    storage.setItem('username', user.username);
                }
                storage.setItem('user_level', user.level);
                if (typeof user.canShare === 'boolean') {
                    storage.setItem('user_can_share', user.canShare ? 'true' : 'false');
                }

                // Notify listeners (e.g. AuthContext)
                if (this.userChangeCallback) {
                    this.userChangeCallback();
                }

                return user;
            }
            return null;
        } catch (error) {
            logger.warn('Bootstrap fetch failed, falling back to local storage:', error);
            return null;
        }
    }

    private async fetchBootstrapWithoutInterceptor() {
        const token = this.tokens?.accessToken;

        if (!token) {
            throw new Error('Cannot fetch bootstrap without access token');
        }

        return axios.get(`${this.api.defaults.baseURL}/api/me`, {
            timeout: 30000,
            headers: {
                Authorization: `Bearer ${token}`,
                'App-Version': 'web'
            }
        });
    }

    async getCurrentUser(): Promise<User | null> {
        try {
            const storage = getAuthStorage();
            const userData = storage.getItem('user_data');
            const storedCanShare = this.getStoredCanShare();
            if (userData) {
                const parsedUser: User = JSON.parse(userData);

                if (storedCanShare !== null) {
                    parsedUser.canShare = storedCanShare;
                } else if (typeof parsedUser.canShare === 'boolean') {
                    storage.setItem('user_can_share', parsedUser.canShare ? 'true' : 'false');
                }

                return parsedUser;
            }

            // Legacy fallback: build user from scattered localStorage keys
            const username = storage.getItem('username');
            const email = storage.getItem('user_email');
            const level = storage.getItem('user_level');

            if (username) {
                const user: User = {
                    id: '1',
                    username: username,
                    level: level || 'user',
                    email: email || username
                };

                if (storedCanShare !== null) {
                    user.canShare = storedCanShare;
                }

                storage.setItem('user_data', JSON.stringify(user));
                return user;
            }

            return null;
        } catch (error) {
            logger.error('Error getting current user:', error);
            return null;
        }
    }
}

export default AuthService; 
